import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import Swal from "sweetalert2";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { user } from "localStorage";
import { getPhotoUrl } from "utils";
import {
  ListPackageChangeRequests,
  ApprovePackageChangeRequest,
  RejectPackageChangeRequest,
  GetPackageChangeRequest,
} from "api/packageChangeRequest";
import { GetCustomerInfo } from "api/customers";
import RequestDetailDialog from "./RequestDetailDialog";
import { CANCEL_REASON_LABEL_KEYS, STATUS_COLOR, fmtDate, statusKey } from "./shared";

const STATUS_OPTIONS = ["pending", "approved", "rejected", "all"];

// Admin queue for manual package edit/cancel requests submitted by roles
// without direct edit/cancel access (e.g. "manager") — see
// backend/routes/packageChangeRequest.js. Approve applies the change
// immediately via the same logic as a direct admin edit/cancel; reject
// discards it. Both notify the requester over WhatsApp.
// Non-admin viewers (e.g. the manager who submitted a request) only ever see
// their own requests here, read-only — approve/reject is admin-only both
// here and on the backend (PATCH /api/package-change-request is not granted
// to manager, see scripts/seed-package-change-request-permissions.js).
// `embedded` — rendered as the Packages page's "Change Requests" tab: no page
// title or outer padding of its own (the Packages PageShell provides them).
// Also the Approvals page's "Parcel Changes" tab, which passes `onChanged` to
// refresh its pending badge after an approve/reject.
export default function PackageChangeRequests({ embedded = false, onChanged }) {
  const { t } = useTranslation();
  const isAdmin = user()?.role === "admin";
  // Requesters mostly come here to see what happened to their requests, which
  // are usually no longer pending — default them to "all".
  const [statusFilter, setStatusFilter] = useState(isAdmin ? "pending" : "all");
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);
  // Server-side page/sort/search — same contract as the Packages list
  // (backend controllers/packageChangeRequest.js#listPaged).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // customerId → customer name for the changes summary; ids that can't be
  // resolved (no /api/customer access, deleted customer) stay as the raw id.
  const [customerNames, setCustomerNames] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, pageSize, sort: JSON.stringify(sort || {}), search };
    if (statusFilter !== "all") params.status = statusFilter;
    if (!isAdmin) params.mine = "true";
    const res = await ListPackageChangeRequests(params);
    const data = res && res.success ? res.data : [];
    setTotal(res && res.success ? res.total || 0 : 0);
    // A 403 from checkPermission comes back as { errors: [...] } — show it
    // instead of an empty list that looks like "no requests".
    setLoadError(res && res.success ? null : (res?.message || res?.errors?.[0]?.title || t("packageChangeRequest.loadFailed")));
    setRows(data);
    setLoading(false);

    const ids = new Set();
    data.forEach((r) => {
      const diff = r.changes?.customerId;
      if (diff?.from) ids.add(String(diff.from));
      if (diff?.to) ids.add(String(diff.to));
    });
    const missing = [...ids].filter((id) => !customerNames[id]);
    if (missing.length === 0) return;
    const found = {};
    await Promise.all(
      missing.map(async (id) => {
        const c = await GetCustomerInfo(id).catch(() => null);
        if (c?.success && c.data?.name) found[id] = c.data.name;
      })
    );
    setCustomerNames((prev) => ({ ...prev, ...found }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- customerNames is a cache, not an input
  }, [statusFilter, isAdmin, page, pageSize, sort, search]);

  useEffect(() => { load(); }, [load]);
  // A new filter or search starts again from the first page.
  useEffect(() => { setPage(0); }, [statusFilter, search]);

  // ?request=<id> (from an approved/rejected notification) — open that request's
  // detail dialog even when it isn't in the current status filter.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestParam = searchParams.get("request");
  useEffect(() => {
    if (!requestParam) return;
    GetPackageChangeRequest(requestParam).then((res) => {
      if (res?.success && res.data) setDetail(res.data);
    });
    setSearchParams((prev) => { prev.delete("request"); return prev; }, { replace: true });
  }, [requestParam, setSearchParams]);

  const handleApprove = async (request) => {
    const confirm = await Swal.fire({
      title: t("packageChangeRequest.approveConfirmTitle"),
      text: t("packageChangeRequest.approveConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("packageChangeRequest.approve"),
      cancelButtonText: t("common.cancel"),
    });
    if (!confirm.isConfirmed) return;

    setBusyId(request._id);
    const res = await ApprovePackageChangeRequest(request._id);
    setBusyId(null);
    Swal.fire({
      icon: res.success ? "success" : "error",
      title: res.success ? t("packageChangeRequest.approveSuccessTitle") : t("packageChangeRequest.errorTitle"),
      text: res.message,
      timer: res.success ? 1800 : undefined,
    });
    if (res.success) { setDetail(null); load(); onChanged?.(); }
  };

  const handleReject = async (request) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: t("packageChangeRequest.rejectPromptTitle"),
      input: "textarea",
      inputPlaceholder: t("packageChangeRequest.rejectPlaceholder"),
      showCancelButton: true,
      confirmButtonText: t("packageChangeRequest.reject"),
      cancelButtonText: t("common.cancel"),
    });
    if (!isConfirmed) return;

    setBusyId(request._id);
    const res = await RejectPackageChangeRequest(request._id, reason || "");
    setBusyId(null);
    Swal.fire({
      icon: res.success ? "success" : "error",
      title: res.success ? t("packageChangeRequest.rejectSuccessTitle") : t("packageChangeRequest.errorTitle"),
      text: res.message,
      timer: res.success ? 1800 : undefined,
    });
    if (res.success) { setDetail(null); load(); onChanged?.(); }
  };

  const describeChanges = (request) => {
    if (request.actionType !== "edit" || !request.changes) return null;
    // One line per field, same as mobile-staff's list card.
    return Object.entries(request.changes).map(([field, diff]) => {
      const fmt = (v) => {
        if (v === undefined || v === null || v === "") return "—";
        if (field === "customerId") return customerNames[String(v)] ?? String(v);
        if (typeof v === "boolean") return v ? t("packageChangeRequest.yes") : t("packageChangeRequest.no");
        if (typeof v === "object") return Object.values(v).filter((x) => x && typeof x !== "object").join(", ") || "—";
        return String(v);
      };
      return `${t(`packageChangeRequest.fields.${field}`, { defaultValue: field })}: ${fmt(diff.from)} → ${fmt(diff.to)}`;
    });
  };

  const reasonText = (r) => (CANCEL_REASON_LABEL_KEYS[r.reason] ? t(CANCEL_REASON_LABEL_KEYS[r.reason]) : r.reason);
  const typeText = (r) => t(r.actionType === "cancel" ? "packageChangeRequest.typeCancel" : "packageChangeRequest.typeEdit");


  const columns = [
    {
      field: "tracking",
      headerName: t("packageChangeRequest.columnPackage"),
      minWidth: 170,
      flex: 0.8,
      valueGetter: (params) => params.row.packageId?.tracking || params.row.packageId,
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{row.packageId?.tracking || row.packageId}</Typography>
          {row.packageId?.customer?.name && (
            <Typography variant="body2" color="text.secondary">{row.packageId.customer.name}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: "actionType",
      headerName: t("packageChangeRequest.columnType"),
      width: 100,
      valueGetter: (params) => typeText(params.row),
      renderCell: ({ row }) => <Chip size="small" variant="outlined" label={typeText(row)} />,
    },
    {
      field: "requestedBy",
      headerName: t("packageChangeRequest.columnRequestedBy"),
      minWidth: 120,
      flex: 0.5,
      valueGetter: (params) => params.row.requestedBy?.name || "",
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body2">{row.requestedBy?.name || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">{row.requestedBy?.role}</Typography>
        </Box>
      ),
    },
    {
      field: "reason",
      headerName: t("packageChangeRequest.columnReason"),
      minWidth: 300,
      flex: 2,
      sortable: false,
      valueGetter: (params) => [reasonText(params.row), ...(describeChanges(params.row) || [])].join(" | "),
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{reasonText(row)}</Typography>
          {row.note && <Typography variant="body2" color="text.secondary">{row.note}</Typography>}
          {describeChanges(row)?.map((line) => (
            <Typography key={line} variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: "break-word" }}>
              {line}
            </Typography>
          ))}
          {row.evidencePhotos?.length > 0 && (
            <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
              {row.evidencePhotos.map((photo) => (
                <a key={photo.path} href={getPhotoUrl(photo)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  <Box
                    component="img"
                    src={getPhotoUrl(photo)}
                    alt=""
                    sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: "8px", border: "1px solid", borderColor: "divider" }}
                  />
                </a>
              ))}
            </Box>
          )}
          {row.status === "rejected" && row.rejectionReason && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>{row.rejectionReason}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: t("packageChangeRequest.columnStatus"),
      width: 120,
      renderCell: ({ row }) => (
        <Chip size="small" color={STATUS_COLOR[row.status] || "default"} label={t(statusKey(row.status))} />
      ),
    },
    {
      field: "createdAt",
      headerName: t("packageChangeRequest.columnDate"),
      width: 170,
      valueGetter: (params) => new Date(params.row.createdAt),
      renderCell: ({ row }) => <Typography variant="body2">{fmtDate(row.createdAt)}</Typography>,
    },
  ];
  if (isAdmin) {
    columns.push({
      field: "actions",
      headerName: "",
      width: 220,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: ({ row }) =>
        row.status === "pending" ? (
          <Box display="flex" gap={1} flexWrap="wrap" onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              disabled={busyId === row._id}
              onClick={() => handleApprove(row)}
            >
              {t("packageChangeRequest.approve")}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              disabled={busyId === row._id}
              onClick={() => handleReject(row)}
            >
              {t("packageChangeRequest.reject")}
            </Button>
          </Box>
        ) : null,
    });
  }

  return (
    <PageShell
      title={embedded ? undefined : t(isAdmin ? "packageChangeRequest.pageTitle" : "packageChangeRequest.myRequestsPageTitle")}
      sx={embedded ? { p: 0, maxWidth: "none" } : undefined}
    >
      {/* Filter card — same pattern as the Packages → Stock tab */}
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <FormControl sx={{ minWidth: 260 }}>
          <InputLabel>{t("packageChangeRequest.columnStatus")}</InputLabel>
          <Select
            label={t("packageChangeRequest.columnStatus")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s === "all" ? t("packageChangeRequest.filterAll") : t(statusKey(s))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </BentoCard>
      {loadError && (
        <Typography color="error" sx={{ mb: 2 }}>{t("packageChangeRequest.loadFailed")} ({loadError})</Typography>
      )}
      <StyledDataGrid
        loading={loading}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        getRowHeight={() => "auto"}
        onRowClick={(params) => setDetail(params.row)}
        pagination
        paginationMode="server"
        sortingMode="server"
        rowCount={total}
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
        onSortModelChange={(model) => setSort(model[0] || {})}
        disableSelectionOnClick
        localeText={{ noRowsLabel: t("packageChangeRequest.noRequests") }}
        components={{ Toolbar: DataGridCustomToolbar }}
        componentsProps={{ toolbar: { searchInput, setSearchInput, setSearch } }}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-cell": { py: 1.25, alignItems: "flex-start", whiteSpace: "normal" },
        }}
      />

      <RequestDetailDialog
        open={!!detail}
        request={detail}
        onClose={() => setDetail(null)}
        isAdmin={isAdmin}
        busy={!!detail && busyId === detail._id}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </PageShell>
  );
}
